/* Shared error-text parsing for useApiService's rejections (UND-987). Originally lived only as
   saveLossChanges.js's saveErrorText for the loss grid's own saves; pulled out here once a second
   caller (the customer-attachment Change dialog) needed the identical parsing, so there is exactly one
   place that knows this shape rather than two copies that can drift.

   Axios rejects a non-2xx before useApiService's own validateResponse can run, and the authenticated
   path re-throws the bare axios response — which carries the server's body on `data` and has no
   `message` at all. So a 400's or 404's field-naming text is legible only through `data`; reading
   `message` first would render every one of them as the generic fallback. */

/* Not every `data` is a sentence the API chose to write: web.tomcat.xml forwards uncaught exceptions
   and route-less 404s to a JSP error page, so those arrive as an entire HTML document, and a 5xx is
   never worth showing verbatim either. Status and shape are both checked because that 404 error page
   answers with a 404, not a 5xx. */
// Any markup (an HTML or XML error page, whatever its first tag) is a page, never a sentence to show.
const isErrorPage = (error) => typeof error.data === 'string' && /^\s*</.test(error.data);

/* `serverErrorText` admits a 5xx's own plain-text body, for an endpoint that answers a failure it
   names (Create Renewal Quote's pricing failure) with a 500; an error page is still never shown. An
   axios error, rather than the bare response, carries the response on `response`. */
export const apiErrorText = (rejection, fallback = 'Something went wrong', { serverErrorText = false } = {}) => {
  const error = rejection && rejection.response ? { ...rejection.response, message: rejection.message } : rejection;
  if (!error || isErrorPage(error) || (error.status >= 500 && !serverErrorText)) {
    return fallback;
  }
  const body = error.data;
  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }
  if (body && typeof body.error === 'string' && body.error) {
    return body.error;
  }
  return error.message || fallback;
};
